'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface DonationOption {
  id: number;
  amount: number;
  impact: string;
  icon: string;
}

interface DonationFrequency {
  id: string;
  label: string;
  description: string;
}

const DonationPortalSection = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState<number>(50);
  const [customAmount, setCustomAmount] = useState<string>('');
  const [frequency, setFrequency] = useState<string>('one-time');
  const [selectedCategory, setSelectedCategory] = useState<string>('general');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const donationOptions: DonationOption[] = [
    {
      id: 1,
      amount: 25,
      impact: 'Provides 5 families with clean water for a month',
      icon: 'BeakerIcon'
    },
    {
      id: 2,
      amount: 50,
      impact: 'Supplies medical care for 10 children',
      icon: 'HeartIcon'
    },
    {
      id: 3,
      amount: 100,
      impact: 'Feeds 20 families for two weeks',
      icon: 'ShoppingBagIcon'
    },
    {
      id: 4,
      amount: 250,
      impact: 'Builds emergency shelter for 3 families',
      icon: 'HomeIcon'
    }
  ];

  const frequencies: DonationFrequency[] = [
    {
      id: 'one-time',
      label: 'One-Time',
      description: 'Make a single donation'
    },
    {
      id: 'monthly',
      label: 'Monthly',
      description: 'Recurring monthly support'
    },
    {
      id: 'quarterly',
      label: 'Quarterly',
      description: 'Every 3 months'
    },
    {
      id: 'annually',
      label: 'Annually',
      description: 'Once per year'
    }
  ];

  const categories = [
    { id: 'general', label: 'Where Most Needed', icon: 'SparklesIcon' },
    { id: 'food', label: 'Food Security', icon: 'ShoppingBagIcon' },
    { id: 'water', label: 'Clean Water', icon: 'BeakerIcon' },
    { id: 'health', label: 'Healthcare', icon: 'HeartIcon' },
    { id: 'education', label: 'Education', icon: 'AcademicCapIcon' },
    { id: 'shelter', label: 'Emergency Shelter', icon: 'HomeIcon' }
  ];

  const handleAmountSelect = (amount: number) => {
    if (!isHydrated) return;
    setSelectedAmount(amount);
    setCustomAmount('');
  };

  const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isHydrated) return;
    const value = e.target.value;
    setCustomAmount(value);
    if (value) {
      setSelectedAmount(0);
    }
  };

  const handleFrequencyChange = (freq: string) => {
    if (!isHydrated) return;
    setFrequency(freq);
  };

  const handleCategoryChange = (category: string) => {
    if (!isHydrated) return;
    setSelectedCategory(category);
  };

  const getCurrentAmount = () => {
    return customAmount ? parseFloat(customAmount) : selectedAmount;
  };

  if (!isHydrated) {
    return (
      <section className="py-20 bg-gradient-to-br from-primary/5 to-secondary/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-headline font-bold text-foreground mb-4">
              Make Your Impact Today
            </h2>
            <p className="text-lg text-muted-foreground font-body">Loading donation portal...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-gradient-to-br from-primary/5 to-secondary/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-headline font-bold text-foreground mb-4">
            Make Your Impact Today
          </h2>
          <p className="text-lg text-muted-foreground font-body max-w-3xl mx-auto">
            Choose your contribution level and see the direct impact of your compassion. Every dollar is tracked transparently from donation to delivery.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <div className="bg-card rounded-xl p-8 shadow-lg border border-border">
              <h3 className="text-2xl font-headline font-bold text-foreground mb-6 flex items-center space-x-2">
                <Icon name="CurrencyDollarIcon" size={28} className="text-primary" variant="solid" />
                <span>Select Donation Amount</span>
              </h3>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {donationOptions.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => handleAmountSelect(option.amount)}
                    className={`p-6 rounded-xl border-2 transition-all duration-300 ${
                      selectedAmount === option.amount && !customAmount
                        ? 'border-primary bg-primary/10 scale-105' :'border-border hover:border-primary/50 hover:bg-muted'
                    }`}
                  >
                    <div className="flex flex-col items-center space-y-2">
                      <Icon
                        name={option.icon as any}
                        size={32}
                        className={selectedAmount === option.amount && !customAmount ? 'text-primary' : 'text-muted-foreground'}
                        variant="solid"
                      />
                      <span className="text-2xl font-headline font-bold text-foreground">
                        ${option.amount}
                      </span>
                    </div>
                  </button>
                ))}
              </div>

              <div className="mb-6">
                <label className="block text-sm font-cta font-bold text-foreground mb-2">
                  Or Enter Custom Amount
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground text-lg">
                    $
                  </span>
                  <input
                    type="number"
                    value={customAmount}
                    onChange={handleCustomAmountChange}
                    placeholder="Enter amount"
                    className="w-full pl-10 pr-4 py-4 border-2 border-border rounded-lg font-body text-lg focus:outline-none focus:border-primary transition-colors duration-300"
                    min="1"
                  />
                </div>
              </div>

              {(selectedAmount > 0 || customAmount) && (
                <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
                  <p className="text-sm font-body text-foreground leading-relaxed">
                    <Icon name="InformationCircleIcon" size={20} className="inline text-primary mr-2" variant="solid" />
                    {donationOptions.find(opt => opt.amount === selectedAmount)?.impact ||
                     `Your generous contribution of $${getCurrentAmount()} will directly support vulnerable communities in need.`}
                  </p>
                </div>
              )}
            </div>

            <div className="bg-card rounded-xl p-8 shadow-lg border border-border">
              <h3 className="text-2xl font-headline font-bold text-foreground mb-6 flex items-center space-x-2">
                <Icon name="CalendarIcon" size={28} className="text-primary" variant="solid" />
                <span>Donation Frequency</span>
              </h3>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {frequencies.map((freq) => (
                  <button
                    key={freq.id}
                    onClick={() => handleFrequencyChange(freq.id)}
                    className={`p-4 rounded-xl border-2 transition-all duration-300 ${
                      frequency === freq.id
                        ? 'border-primary bg-primary/10' :'border-border hover:border-primary/50 hover:bg-muted'
                    }`}
                  >
                    <div className="text-center">
                      <div className="font-cta font-bold text-foreground mb-1">
                        {freq.label}
                      </div>
                      <div className="text-xs text-muted-foreground font-body">
                        {freq.description}
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              {frequency !== 'one-time' && (
                <div className="mt-4 bg-success/10 border border-success/30 rounded-lg p-4">
                  <p className="text-sm font-body text-foreground">
                    <Icon name="CheckCircleIcon" size={20} className="inline text-success mr-2" variant="solid" />
                    Recurring donations provide sustainable support and maximize your impact over time.
                  </p>
                </div>
              )}
            </div>

            <div className="bg-card rounded-xl p-8 shadow-lg border border-border">
              <h3 className="text-2xl font-headline font-bold text-foreground mb-6 flex items-center space-x-2">
                <Icon name="TagIcon" size={28} className="text-primary" variant="solid" />
                <span>Support Category</span>
              </h3>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => handleCategoryChange(category.id)}
                    className={`p-4 rounded-xl border-2 transition-all duration-300 ${
                      selectedCategory === category.id
                        ? 'border-primary bg-primary/10' :'border-border hover:border-primary/50 hover:bg-muted'
                    }`}
                  >
                    <div className="flex flex-col items-center space-y-2">
                      <Icon
                        name={category.icon as any}
                        size={28}
                        className={selectedCategory === category.id ? 'text-primary' : 'text-muted-foreground'}
                        variant="solid"
                      />
                      <span className="text-sm font-cta font-bold text-foreground text-center">
                        {category.label}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-card rounded-xl p-8 shadow-lg border border-border sticky top-24">
              <h3 className="text-2xl font-headline font-bold text-foreground mb-6">
                Donation Summary
              </h3>

              <div className="space-y-4 mb-6">
                <div className="flex items-center justify-between pb-4 border-b border-border">
                  <span className="text-muted-foreground font-body">Amount</span>
                  <span className="text-2xl font-headline font-bold text-primary">
                    ${getCurrentAmount()}
                  </span>
                </div>

                <div className="flex items-center justify-between pb-4 border-b border-border">
                  <span className="text-muted-foreground font-body">Frequency</span>
                  <span className="font-cta font-bold text-foreground capitalize">
                    {frequency.replace('-', ' ')}
                  </span>
                </div>

                <div className="flex items-center justify-between pb-4 border-b border-border">
                  <span className="text-muted-foreground font-body">Category</span>
                  <span className="font-cta font-bold text-foreground text-right text-sm">
                    {categories.find(c => c.id === selectedCategory)?.label}
                  </span>
                </div>

                <div className="bg-success/10 border border-success/30 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon name="ShieldCheckIcon" size={20} className="text-success" variant="solid" />
                    <span className="text-sm font-cta font-bold text-success">
                      100% Tax Deductible
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground font-body">
                    Your donation is fully tax-deductible. Receipt will be sent immediately.
                  </p>
                </div>
              </div>

              <button className="w-full px-6 py-4 bg-primary text-primary-foreground rounded-lg font-cta font-bold text-lg transition-all duration-300 hover:scale-105 hover:shadow-lg pulse-gentle flex items-center justify-center space-x-2 mb-4">
                <Icon name="HeartIcon" size={24} variant="solid" />
                <span>Complete Donation</span>
              </button>

              <div className="text-center">
                <p className="text-xs text-muted-foreground font-body mb-2">
                  Secure payment powered by industry-leading encryption
                </p>
                <div className="flex items-center justify-center space-x-2">
                  <Icon name="LockClosedIcon" size={16} className="text-success" variant="solid" />
                  <span className="text-xs font-cta font-bold text-success">
                    PCI-DSS Compliant
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DonationPortalSection;